import styled from "@emotion/styled";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useOutletContext, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { helpHttp } from "../../helper/helpHttp";
import { getProductVideo } from "../../utils/productVideo";
import { scrollToId } from "../../utils/scrollToId";
import { eliminarDiacriticosEs } from "../../utils/textConverter";
import { urlToId } from "../../utils/urlToId";
import { useDispatch } from "react-redux";
import { changeLoading } from "../../redux/slices/category";
import LoopIcon from "@mui/icons-material/Loop";
import Button from "@mui/material/Button";
import CachedIcon from "@mui/icons-material/Cached";

const ProductData = () => {
  const [data, setData] = useState(undefined);
  const { producto } = useParams();
  const { get } = helpHttp();
  const introductionVideoUrl = getProductVideo(producto);
  const { hash } = useLocation();
  const dispatch = useDispatch();
  const id = urlToId(producto).id;
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    dispatch(changeLoading(true));
    get(`categorias/getDetailsCategoriaSinPdf/${id}`)
      .then((data) => {
        setData(data);
      })
      .catch(() => toast.error("Ha ocurrido un error"))
      .finally(() => {
        if (hash) {
          setTimeout(() => {
            scrollToId(hash);
          }, 500);
        }
        dispatch(changeLoading(false));
      });
  }, []);

  const downloadPdf = (itemId, fileName) => {
    setDownloadingPdf(true);
    get(`categorias/getDetailCategoriaByIdDetail/${id}/${itemId}`)
      .then((response) => {
        const isMobile =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          ) || window.matchMedia("(pointer: coarse)").matches;

        if (isMobile) {
          const base64 = response.datos.url_pdf.split(",")[1]; // Quitar "data:application/pdf;base64,"
          const byteCharacters = atob(base64);
          const byteNumbers = new Array(byteCharacters.length)
            .fill()
            .map((_, i) => byteCharacters.charCodeAt(i));
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf" });

          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl);
        } else {
          const pdfData = response.datos.url_pdf;
          const pdfLink = document.createElement("a");
          pdfLink.href = pdfData;
          pdfLink.download = `${fileName}.pdf`;
          document.body.appendChild(pdfLink);
          pdfLink.click();
          document.body.removeChild(pdfLink);
        }
      })
      .catch(() => {
        toast.error("No se pudo abrir el PDF");
      })
      .finally(() => {
        setDownloadingPdf(false);
      });
  };

  return (
    <StyledList>
      {introductionVideoUrl && (
        <StyledCard>
          <StyledVideo>
            <iframe
              width="100%"
              height="100%"
              src={introductionVideoUrl}
              frameBorder="0"
              loading="lazy"
              allow="fullscreen; picture-in-picture"
            />
          </StyledVideo>
          <StyledInfo>
            <Typography component={"h3"} variant="title">
              Introducción
            </Typography>
          </StyledInfo>
        </StyledCard>
      )}
      {data?.datos?.map((e) => (
        <StyledCard key={e.id} id={eliminarDiacriticosEs(e.titulo, true)}>
          <StyledVideo>
            <iframe
              width="100%"
              height="100%"
              src={e.url_video}
              frameBorder="0"
              loading="lazy"
              allow="fullscreen; picture-in-picture"
            />
          </StyledVideo>
          <StyledInfo>
            <Typography component={"h3"} variant="title">
              {e.titulo}
            </Typography>
            <div>
              <Typography>{e.descripcion}</Typography>
              {e.url_pdf && (
                <Button
                  disabled={downloadingPdf}
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={() => downloadPdf(e.id, e.titulo)}
                  sx={{ marginTop: "15px", width: "max-content" }}
                  endIcon={
                    downloadingPdf ? (
                      <CachedIcon className="downloadingPdf" />
                    ) : (
                      <PictureAsPdfOutlinedIcon />
                    )
                  }
                >
                  Descargar
                </Button>
              )}
            </div>
          </StyledInfo>
        </StyledCard>
      ))}
      {data?.codigo === "404" && (
        <Typography component={"h4"} variant="title">
          No se encontraron resultados
        </Typography>
      )}
    </StyledList>
  );
};

export default ProductData;

const StyledVideo = styled.div`
  aspect-ratio: 16/9;
  flex: 1;
  flex-basis: 360px;
  @media (min-width: 1100px) {
    max-width: 400px;
  }
`;

const StyledCard = styled.div`
  display: flex;
  background-color: white;
  border-radius: 20px;
  padding: 20px;
  @media (min-width: 768px) {
    padding: 30px;
  }
  flex-wrap: wrap;
  width: 100%;
  gap: 10px 20px;
`;
const StyledInfo = styled.div`
  display: flex;
  flex: 2;
  flex-basis: 360px;
  flex-direction: column;
  gap: 10px;
  > h3 {
    font-size: 26px;
  }
`;

const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 10px;
  > h4 {
    text-align: center;
    font-size: 2rem;
  }
`;
